import { message } from 'antd'
import dayjs from 'dayjs'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { AssetDrawingsData, RevisionMetadata } from 'features/reporting/types'
import {
  METHOD_TO_PROCEDURE,
  METHOD_TO_PROCEDURE_TITLE,
} from 'pages/reporting/reports/rug/constants/inspectionInfo'
import { CantileverReportDataResult } from 'pages/reporting/reports/rug/hooks/useCantileverReportData'
import {
  CoverSectionData,
  DataSheetSectionData,
  DEFAULT_USE_TABLE_FOR_SUMMARY,
  ImagesSectionData,
  MainInfo,
  RUGFieldSources,
  RugFormDataState,
  RUGPrePopulatedData,
  SectionAddendum,
  SummarySectionData,
} from 'pages/reporting/reports/rug/rugReportData'
import { addendumDraftSchema } from 'pages/reporting/reports/rug/utils/addendums/addendumContent'
import {
  normalizeInspectionTemperature,
  serializeInspectionTemperatureForStorage,
} from 'pages/reporting/reports/rug/utils/inspectionTemperature'
import { normalizeApprovalWorkflow } from 'pages/reporting/shared/approval/approvalRules'
import { convertDatesToDay } from 'pages/reporting/shared/utils/dateUtils'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  GeckoMeasurement,
  GeckoMeasurementJson,
  GeckoReportingMetadata,
  ReportApprovalWorkflow,
  ReportESignatureState,
  SignatureData,
  SignoffState,
} from 'types'

export function deserializeGeckoMeasurementsAndDates<T>(data: T): T {
  const isGeckoMeasurementJson = (
    obj: unknown
  ): obj is GeckoMeasurementJson => {
    if (obj === null || typeof obj !== 'object') return false

    if (!('num' in obj) || !('unit' in obj)) return false

    const candidate = obj as Record<string, unknown>

    return (
      typeof candidate.num === 'number' && typeof candidate.unit === 'string'
    )
  }

  if (data === null || data === undefined) return data

  if (Array.isArray(data)) {
    return data.map((item) =>
      deserializeGeckoMeasurementsAndDates(item)
    ) as unknown as T
  }

  if (typeof data === 'string') {
    // Dayjs can't validate the UTC indicator Z,
    // so check for it, then remove it and try ISO8601 validating
    if (data.includes('Z')) {
      if (
        dayjs(
          data.slice(0, data.length - 1),
          'YYYY-MM-DDTHH:mm:ss.SSS',
          true
        ).isValid()
      ) {
        return dayjs(data) as unknown as T
      }
    }
  }

  if (typeof data === 'object') {
    if (isGeckoMeasurementJson(data)) {
      return GeckoMeasurement.CreateFromJSON({
        num: data.num,
        unit: data.unit,
      }) as unknown as T
    }

    const result: Record<string, unknown> = {}
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        result[key] = deserializeGeckoMeasurementsAndDates(
          (data as Record<string, unknown>)[key]
        )
      }
    }
    return result as unknown as T
  }

  return data
}

export const getSignoffFieldForRole = (
  role: SignatureData['role']
): keyof Pick<
  SignoffState,
  'supervisorSignoff' | 'dataAnalystSignoff' | 'reviewerSignoff'
> => {
  switch (role) {
    case 'supervisor':
      return 'supervisorSignoff'
    case 'dataAnalyst':
      return 'dataAnalystSignoff'
    case 'reviewer':
      return 'reviewerSignoff'
  }

  throw new Error(`Unsupported signoff role: ${role}`)
}

export const buildUpdatedSignoffState = (
  currentSignoff: SignoffState | undefined,
  signatureData: SignatureData
): SignoffState => {
  const updatedSignoff: SignoffState = {
    ...(currentSignoff || { lastRevision: 0 }),
    [getSignoffFieldForRole(signatureData.role)]: signatureData,
  }

  if (
    signatureData.role === 'reviewer' &&
    updatedSignoff.supervisorSignoff &&
    currentSignoff?.reviewerSignoff !== signatureData
  ) {
    updatedSignoff.lastRevision = (updatedSignoff.lastRevision || 0) + 1
  }

  return updatedSignoff
}

export const serializeMainInfoForStorage = (values: MainInfo) => {
  const { inspectionTemperature, ...rest } = values

  return {
    ...rest,
    ...serializeInspectionTemperatureForStorage(inspectionTemperature),
  }
}

const normalizeMainInfoJobNumber = (
  values: Partial<MainInfo> & { assetId?: unknown }
): Partial<MainInfo> => {
  const { assetId, ...rest } = values
  const legacyAssetId = typeof assetId === 'string' ? assetId : undefined

  return {
    ...rest,
    jobNumber: rest.jobNumber ?? legacyAssetId,
  }
}

export function getSelectedFormData(
  metadataEntryId: string,
  metadataEntries: readonly GeckoReportingMetadata[],
  prePopulatedData?: RUGPrePopulatedData
) {
  const currentEntry = metadataEntries.find(
    (entry) => entry.id === metadataEntryId
  )

  if (!currentEntry) {
    console.warn('Current metadata entry not found')
    return
  }

  const existingData = currentEntry.data as Record<string, unknown>
  const deserializedEntry = deserializeGeckoMeasurementsAndDates(
    existingData
  ) as Record<string, unknown>
  const sectionInfo =
    deserializedEntry.section_info as Partial<RugFormDataState> &
      Record<string, unknown>

  const mergeWithPrePopulated = <T>(
    stored: T | undefined,
    prepopulated: Partial<T> | undefined
  ): T => {
    if (!prepopulated) return (stored || {}) as T
    if (!stored) return prepopulated as T

    const storedEntries = Object.entries(stored as Record<string, unknown>)
    const populatedStoredEntries = storedEntries.filter(([, value]) => {
      if (typeof value === 'string') return value.trim().length > 0
      return value !== undefined && value !== null && value !== ''
    })

    return {
      ...prepopulated,
      ...Object.fromEntries(populatedStoredEntries),
    } as T
  }

  const mergedMainInfo = mergeWithPrePopulated<MainInfo>(
    normalizeMainInfoJobNumber(
      (sectionInfo.mainInfo as Partial<MainInfo> & { assetId?: unknown }) || {}
    ) as MainInfo,
    normalizeMainInfoJobNumber(prePopulatedData || {}) as Partial<MainInfo>
  )
  const mergedMainInfoRecord = mergedMainInfo as MainInfo & {
    inspectionTemperatureUnit?: unknown
  }

  const storedDataSheetSectionData = sectionInfo.dataSheetSectionData as
    | Partial<DataSheetSectionData>
    | undefined
  const mergedDataSheetSectionData =
    mergeWithPrePopulated<DataSheetSectionData>(
      storedDataSheetSectionData as DataSheetSectionData | undefined,
      prePopulatedData
    )
  const procedure =
    mergedDataSheetSectionData.procedure === METHOD_TO_PROCEDURE_TITLE.rug
      ? METHOD_TO_PROCEDURE.rug
      : (mergedDataSheetSectionData.procedure ?? METHOD_TO_PROCEDURE.rug)
  const procedureTitle =
    mergedDataSheetSectionData.procedureTitle ?? METHOD_TO_PROCEDURE_TITLE.rug
  const storedSensorSerialNumbers =
    storedDataSheetSectionData?.sensorSerialNumbers
  const sensorSerialNumbers =
    storedSensorSerialNumbers !== undefined
      ? storedSensorSerialNumbers
      : (prePopulatedData?.sensorSerialNumbers ?? {})

  const newFormData = {
    mainInfo: {
      ...mergedMainInfo,
      inspectionTemperature: normalizeInspectionTemperature(
        mergedMainInfoRecord.inspectionTemperature,
        mergedMainInfoRecord.inspectionTemperatureUnit
      ),
    },
    coverSectionData: convertDatesToDay(
      mergeWithPrePopulated<CoverSectionData>(
        sectionInfo.coverSectionData as CoverSectionData,
        prePopulatedData
      ) as unknown as Record<string, unknown>
    ) as unknown as CoverSectionData,
    summarySectionData: {
      useTableForSummary: DEFAULT_USE_TABLE_FOR_SUMMARY,
      includeTMin: true,
      ...mergeWithPrePopulated<SummarySectionData>(
        sectionInfo.summarySectionData as SummarySectionData,
        prePopulatedData
      ),
    },
    dataSheetSectionData: {
      ...mergedDataSheetSectionData,
      procedure,
      procedureTitle,
      sensorSerialNumbers,
    },
    imagesSectionData:
      (sectionInfo.imagesSectionData as ImagesSectionData) ||
      ({} as ImagesSectionData),
    assetDrawings: (sectionInfo.assetDrawings as AssetDrawingsData) || {
      images: {},
    },
    signoff: (existingData.signoff as SignoffState) || {
      lastRevision: 0,
    },
    approvalWorkflow: normalizeApprovalWorkflow(
      existingData.approvalWorkflow as ReportApprovalWorkflow | undefined
    ),
    eSignatures: existingData.eSignatures as ReportESignatureState | undefined,
    revisionMetadata: (existingData.revisionMetadata as RevisionMetadata) || {
      revisionNumber: 1,
      createdAt: new Date().toISOString(),
      isPublished: false,
    },
    publishedPdfPath: (existingData.publishedPdfPath as string) || '',
    sectionAddendums: (sectionInfo.sectionAddendums as SectionAddendum[]) || [],
    addendumDraft: addendumDraftSchema.safeParse(sectionInfo.addendumDraft)
      .data,
  }

  return newFormData
}

export const useFormData = (
  metadataEntryId: string,
  metadataEntries: readonly GeckoReportingMetadata[],
  isLoading: boolean,
  prePopulatedData?: CantileverReportDataResult['prePopulatedData'],
  prePopulatedFieldSources: RUGFieldSources = {}
) => {
  const [formData, setFormData] = useState<RugFormDataState>({
    mainInfo: {
      assetName: '',
      jobNumber: '',
      assetType: '',
      assetMaterial: '',
      customerName: '',
      customerLocation: '',
      inspectionTemperature: undefined,
      componentAxisStartLocations: {},
    },
    coverSectionData: {
      inspectionStartDate: '',
      inspectionEndDate: '',
    },
    summarySectionData: {
      inspectionResolution: {
        height: 0,
        width: 0,
        widthUnit: '',
        heightUnit: '',
      },
      deliverableResolution: {
        height: 0,
        width: 0,
        widthUnit: '',
        heightUnit: '',
      },
      coverageScope: '',
      corrosionBLUF: '',
      useTableForSummary: DEFAULT_USE_TABLE_FOR_SUMMARY,
      includeTMin: true,
    },
    dataSheetSectionData: {
      sensorSerialNumbers: {},
      inspectionSurface: '',
      surfaceCondition: '',
      coatingPresent: false,
      robots: [],
      calibrationBlocks: [],
      calibrationTimes: [],
      calibrationSteps: [],
    },
    imagesSectionData: {
      imageReportIds: [],
    },
    assetDrawings: {
      images: {},
    },
    signoff: {
      lastRevision: 0,
    },
    revisionMetadata: {
      revisionNumber: 1,
      createdAt: new Date().toISOString(),
      isPublished: false,
    },
    publishedPdfPath: '',
    sectionAddendums: [],
  })
  const formDataRef = useRef(formData)
  const activeMetadataEntryIdRef = useRef(metadataEntryId)
  activeMetadataEntryIdRef.current = metadataEntryId
  const initializedMetadataEntryIdRef = useRef<string | undefined>(undefined)
  const [fieldSources, setFieldSources] = useState<RUGFieldSources>({})
  const [isSavingAddendums, setIsSavingAddendums] = useState(false)
  const addendumSavePendingRef = useRef(false)

  const { rolodexPatch } = useRolodexEdit()

  useEffect(() => {
    formDataRef.current = formData
  }, [formData])

  useEffect(() => {
    if (
      isLoading ||
      initializedMetadataEntryIdRef.current === metadataEntryId
    ) {
      return
    }

    const newFormData = getSelectedFormData(
      metadataEntryId,
      metadataEntries,
      prePopulatedData
    )
    if (newFormData) {
      setFormData(newFormData)
      setFieldSources(prePopulatedFieldSources)
      initializedMetadataEntryIdRef.current = metadataEntryId
    }
  }, [
    isLoading,
    metadataEntries,
    metadataEntryId,
    prePopulatedData,
    prePopulatedFieldSources,
  ])

  const updateFormData = useCallback((newData: Partial<RugFormDataState>) => {
    setFormData((prev) => {
      const nextFormData = { ...prev, ...newData }
      formDataRef.current = nextFormData
      return nextFormData
    })
  }, [])

  const handleMainInfoFinish = useCallback(
    async (
      values: MainInfo,
      options: { showSuccessMessage?: boolean } = {}
    ) => {
      try {
        updateFormData({ mainInfo: values })
        const storageValues = serializeMainInfoForStorage(values)

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: { mainInfo: storageValues },
            },
          },
        ])

        if (options.showSuccessMessage !== false) {
          message.success('Main info saved successfully!')
        }
      } catch (error) {
        console.error('Failed to save main info', error)
        message.error('Failed to save main info')
      }
    },
    [updateFormData, metadataEntryId, rolodexPatch]
  )

  const handleCoverSectionFinish = useCallback(
    async (values: CoverSectionData) => {
      try {
        const storageValues = {
          ...values,
          inspectionStartDate: values.inspectionStartDate,
          inspectionEndDate: values.inspectionEndDate,
        }

        updateFormData({
          coverSectionData: values,
        })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                coverSectionData: storageValues,
              },
            },
          },
        ])
        message.success('Cover section saved successfully!')
      } catch {
        message.error('Failed to save cover section')
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handleSummarySectionFinish = useCallback(
    async (values: SummarySectionData) => {
      try {
        const summarySectionData = {
          ...formDataRef.current.summarySectionData,
          ...values,
        }

        updateFormData({
          summarySectionData,
        })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                summarySectionData,
              },
            },
          },
        ])
        message.success('Summary section saved successfully!')
      } catch (error) {
        console.error('Error saving summary section:', error)
        message.error('Failed to save summary section')
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handleDataSheetSectionFinish = useCallback(
    async (values: DataSheetSectionData) => {
      try {
        const storageValues = {
          ...values,
          sensorSerialNumbers: values.sensorSerialNumbers || {},
          calibrationBlocks: values.calibrationBlocks || [],
          calibrationTimes: values.calibrationTimes || [],
        }

        updateFormData({ dataSheetSectionData: storageValues })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                dataSheetSectionData: storageValues,
              },
            },
          },
        ])

        message.success('Data sheet section saved successfully')
      } catch (error) {
        console.error('Failed to save data sheet section:', error)
        message.error('Failed to save data sheet section')
        throw error
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handleImagesSectionFinish = useCallback(
    async (values: ImagesSectionData) => {
      try {
        updateFormData({ imagesSectionData: values })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                imagesSectionData: values,
              },
            },
          },
        ])
        message.success('Images section saved successfully!')
      } catch {
        message.error('Failed to save images section')
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handleAssetDrawingsFinish = useCallback(
    async (
      imagePath: string,
      imageType: 'heatmap' | 'no-heatmap' | 'heatmap-with-indications' | string,
      index?: number,
      imageCaption?: string
    ) => {
      try {
        const updatedImages = {
          ...formData.assetDrawings.images,
          [imageType]: {
            imagePath,
            imageCaption,
            type: imageType,
            index,
          },
        }

        updateFormData({
          assetDrawings: {
            images: updatedImages,
          },
        })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                assetDrawings: {
                  images: updatedImages,
                },
              },
            },
          },
        ])
        message.success('Asset drawing saved successfully!')
      } catch {
        message.error('Failed to save asset drawing')
      }
    },
    [
      metadataEntryId,
      formData.assetDrawings.images,
      updateFormData,
      rolodexPatch,
    ]
  )

  const handleSectionAddendumsFinish = useCallback(
    async (sectionAddendums: SectionAddendum[]) => {
      if (addendumSavePendingRef.current)
        throw new Error('Wait for addendums to finish saving')
      if (activeMetadataEntryIdRef.current !== metadataEntryId)
        throw new Error(
          'The selected revision changed. Save the current revision instead.'
        )
      const current = formDataRef.current
      if (
        current.revisionMetadata.isPublished ||
        Boolean(current.publishedPdfPath) ||
        current.eSignatures?.records.length
      ) {
        throw new Error(
          'Cannot edit addendums on a published or signed report. Void signatures before editing.'
        )
      }
      try {
        addendumSavePendingRef.current = true
        setIsSavingAddendums(true)
        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                sectionAddendums,
              },
            },
          },
        ])
        if (activeMetadataEntryIdRef.current === metadataEntryId) {
          updateFormData({ sectionAddendums })
          message.success('Section addendums saved successfully')
        }
      } catch (error) {
        message.error('Failed to save section addendums')
        throw error
      } finally {
        addendumSavePendingRef.current = false
        setIsSavingAddendums(false)
      }
    },
    [updateFormData, metadataEntryId, rolodexPatch]
  )

  const handleSignoffFinish = useCallback(
    async (signatureData: SignatureData): Promise<SignoffState> => {
      try {
        const updatedSignoff = buildUpdatedSignoffState(
          formDataRef.current.signoff,
          signatureData
        )

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              signoff: updatedSignoff,
            },
          },
        ])

        updateFormData({
          signoff: updatedSignoff,
        })

        return updatedSignoff
      } catch (error) {
        console.error('Failed to save signoff:', error)
        throw error
      }
    },
    [metadataEntryId, rolodexPatch, updateFormData]
  )

  return {
    formData,
    fieldSources,
    updateFormData,
    handleMainInfoFinish,
    handleCoverSectionFinish,
    handleSummarySectionFinish,
    handleDataSheetSectionFinish,
    handleImagesSectionFinish,
    handleAssetDrawingsFinish,
    handleSectionAddendumsFinish,
    isSavingAddendums,
    handleSignoffFinish,
  }
}
